'use client';

import { useMemo, useState } from 'react';

import Link from 'next/link';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import SecondaryButton from '@/components/ui/Button/SecondaryButton';
import RichText from '@/components/ui/RichText/RichText';

import type {
  ContentCardsSectionButton,
  ContentCardsSectionData,
} from './ContentCardsSection.types';

type ContentCardsSectionProps = {
  data: ContentCardsSectionData;
  className?: string;
  testIdPrefix?: string;
  gridClassName?: string;
  cardClassName?: string;
  collapsedCardHeightClassName?: string;
  collapseMaxHeightClassName?: string;
  longContentCharacterLimit?: number;
  longContentBulletLimit?: number;
};

const markdownLinkPattern = /^\[([^\]]+)\]\(([^)]+)\)$/;

const getButtonContent = (button: ContentCardsSectionButton) => {
  const markdownLinkMatch = button.content.match(markdownLinkPattern);

  return {
    href: markdownLinkMatch?.[2] ?? button.link ?? '#',
    label: markdownLinkMatch?.[1] ?? button.content,
  };
};

const isLongCardContent = (
  content: string,
  characterLimit: number,
  bulletLimit: number,
) => {
  const lines = content
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const bulletCount = lines.filter((line) =>
    /^([-*+]|\d+\.)\s+/.test(line),
  ).length;

  return content.length > characterLimit || bulletCount > bulletLimit;
};

const ContentCardsSection = ({
  data,
  className = '',
  testIdPrefix = 'content-cards',
  gridClassName = '',
  cardClassName = '',
  collapsedCardHeightClassName = 'h-[300px]',
  collapseMaxHeightClassName = 'max-h-44',
  longContentCharacterLimit = 220,
  longContentBulletLimit = 5,
}: ContentCardsSectionProps) => {
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {},
  );

  const toggleCard = (cardId: string) => {
    setExpandedCards((current) => ({
      ...current,
      [cardId]: !current[cardId],
    }));
  };

  const cardCount = data.cards.length;

  const collapsibleCards = useMemo(
    () =>
      data.cards.map(
        (card) =>
          card.collapsible ??
          isLongCardContent(
            card.description.content,
            longContentCharacterLimit,
            longContentBulletLimit,
          ),
      ),
    [data.cards, longContentCharacterLimit, longContentBulletLimit],
  );

  const sectionHasCollapsibleCards = collapsibleCards.some(Boolean);

  return (
    <section
      data-testid={`${testIdPrefix}-section`}
      className={`content-wrapper py-8 md:py-10 ${className}`}
    >
      <div data-testid={`${testIdPrefix}-heading`} className="mb-8 md:mb-10">
        <RichText styles={data.heading.styles}>{data.heading.content}</RichText>
        {data.description?.content ? (
          <div
            data-testid={`${testIdPrefix}-description`}
            className="mt-3 md:mt-4"
          >
            <RichText styles={data.description.styles}>
              {data.description.content}
            </RichText>
          </div>
        ) : null}
      </div>

      <div
        data-testid={`${testIdPrefix}-grid`}
        className={`mx-auto grid grid-cols-1 items-stretch gap-8 sm:grid-cols-2 ${
          cardCount === 2
            ? 'lg:grid-cols-2'
            : cardCount === 3
              ? 'lg:grid-cols-3'
              : 'lg:grid-cols-4'
        } ${gridClassName}`}
      >
        {data.cards.map((card, index) => {
          const cardId = `${testIdPrefix}-card-${index}`;
          const isExpanded = expandedCards[cardId] ?? false;
          const shouldCollapse = collapsibleCards[index];
          const isCollapsed = shouldCollapse && !isExpanded;

          const cardHeightClassName = sectionHasCollapsibleCards
            ? isExpanded
              ? 'h-auto'
              : collapsedCardHeightClassName
            : 'h-full';

          const descriptionClassName = [
            'relative min-h-0',
            sectionHasCollapsibleCards && 'flex-1',
            isCollapsed && `${collapseMaxHeightClassName} overflow-hidden`,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <article
              key={`${card.heading.content}-${index}`}
              data-testid={`${testIdPrefix}-card`}
              className={`flex h-full w-full flex-col rounded-[16px] bg-white p-8 shadow-[0_12px_24px_rgba(0,0,0,0.18)] ${cardHeightClassName} ${cardClassName}`}
            >
              <div
                data-testid={`${testIdPrefix}-card-heading`}
                className="shrink-0"
              >
                <RichText styles={card.heading.styles}>
                  {card.heading.content}
                </RichText>
              </div>

              <div
                data-testid={`${testIdPrefix}-card-description`}
                id={`${cardId}-description`}
                className={descriptionClassName}
              >
                <div>
                  <RichText styles={card.description.styles}>
                    {card.description.content}
                  </RichText>
                </div>

                {isCollapsed && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-white to-transparent" />
                )}
              </div>

              {(shouldCollapse || card.button) && (
                <div className="mt-auto shrink-0 bg-white pt-4">
                  {shouldCollapse && (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-controls={`${cardId}-description`}
                      onClick={() => toggleCard(cardId)}
                      className="text-small text-theme-action-primary cursor-pointer font-medium underline-offset-4 transition hover:underline"
                    >
                      {isExpanded ? 'View less' : 'View more'}
                    </button>
                  )}

                  {card.button && (
                    <Link
                      href={getButtonContent(card.button).href}
                      data-testid={`${testIdPrefix}-card-button`}
                      className={`text-small text-theme-action-primary mt-5 inline-flex font-medium no-underline transition hover:underline ${
                        card.button.styles?.className ?? ''
                      }`}
                    >
                      <RichText
                        styles={{
                          ...card.button.styles,
                          className: '',
                          p:
                            card.button.styles?.p ??
                            'm-0 text-small font-medium text-theme-action-primary',
                        }}
                      >
                        {getButtonContent(card.button).label}
                      </RichText>
                    </Link>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {!!data.buttons?.length && (
        <div
          data-testid={`${testIdPrefix}-buttons-wrapper`}
          className="tablet:flex-row tablet:gap-8 mt-10 flex flex-col items-center justify-center gap-4"
        >
          {data.buttons.map((button, index) => {
            const { href, label } = getButtonContent(button);
            const isSecondary = button.variant === 'secondary';

            if (isSecondary) {
              return (
                <SecondaryButton
                  key={`${button.content}-${index}`}
                  data={{
                    title: label,
                    url: href,
                    target: button.target,
                  }}
                  target={button.target}
                  className={`text-small tablet:w-auto min-h-14 w-full min-w-67.5 rounded-md px-8 font-medium ${
                    button.styles?.className ?? ''
                  }`}
                />
              );
            }

            return (
              <PrimaryButton
                key={`${button.content}-${index}`}
                data={{ title: label, link: href }}
                className={button.styles?.className ?? ''}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ContentCardsSection;
